/** @format */

import React, { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react';
import { makeStyles, TableRow } from '@material-ui/core';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import PlatformMsisdnPoolNumber from '../../../interfaces/platform/PlatformMsisdnPoolNumber';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { PlatformResellerCollectionInterface } from '../../../interfaces/collections/PlatformResellerCollectionInterface';
import TableBody from '@material-ui/core/TableBody';
import { useDispatch, useSelector } from 'react-redux';
import { StorageReducerAction, StorageReducerType } from '../../../modules/storage/StorageReducers';
import { PlusOrdersCommonResponseCollectionInterface } from '../../../interfaces/collections/PlusOrdersCommonResponseCollectionInterface';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import TableCell from '@material-ui/core/TableCell';
import { Auth, tokenInfo } from '../../../modules/auth/Auth';
import I18n from '../../../components/Translate/I18n';
import { DateHelper } from '../../../helpers/DateHelper';
import PagerRange from '../../../components/Adescom/PagerRange';
import TableFooter from '@material-ui/core/TableFooter';
import { MvnoBlockListCollectionInterface } from '../../../interfaces/collections/MvnoBlockListCollectionInterface';
import BlockOperationButton from '../../../components/Buttons/BlockOperationButton';
import { history } from '../../../core/history';
import SimCardButton from '../../../components/Buttons/SimCardButton';
import ViewButton from '../../../components/Buttons/ViewButton';
import { PlusOrdersCommonResponse } from '../../../interfaces/platform/PlusOrdersCommonResponse';

const useStyle = makeStyles(adescomStyle);

type MsisdnPlkHistoryItemType = {
    msisdn: PlatformMsisdnPoolNumber;
    mvnoBlockList: MvnoBlockListCollectionInterface;
    filters: Array<Filter>;
    orders: Array<Order>;
    platformResellerColection: PlatformResellerCollectionInterface;
    viewPlkMessage(item: PlusOrdersCommonResponse): void;
};

const firstLoad = (dispatch: any, filters: Array<Filter>, orders: Array<Order>): void => {
    const itemLoader = new ItemLoaders(dispatch);
    itemLoader.getListWithCallback(
        FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/orders_common_responses')),
        'load_plk_storage_history_list',
        filters,
        orders,
        (data: PlusOrdersCommonResponseCollectionInterface): void => {
            dispatch({
                type: StorageReducerAction.LOAD_STORAGE_HISTORY_PLK_LIST,
                plusOrdersCommonResponseCollection: data,
            } as StorageReducerType);
        },
        { propagateDataToCallback: true, dispatchAfterCallback: false }
    );
};

export const MsisdnPlkHistoryItem: FunctionComponent<MsisdnPlkHistoryItemType> = ({
    msisdn,
    filters,
    orders,
    platformResellerColection,
    mvnoBlockList,
    viewPlkMessage,
}): React.ReactElement => {
    const { plusOrdersCommonResponseCollection, type } = useSelector((state: { storageReducers: StorageReducerType }) => state.storageReducers);
    const [plkHistory, setPlkHistory] = useState<PlusOrdersCommonResponseCollectionInterface | null>(null);
    const dispatch = useDispatch();
    const classes = useStyle();

    const tableCellRoot = { root: classes.tableCellRootBordered };
    const tableRowRoot = { root: classes.tableRowRoot };
    const tableCellSticky = { root: classes.tableControlSticky };

    useEffect(() => {
        if (type === StorageReducerAction.LOAD_STORAGE_HISTORY_PLK_LIST) {
            if (plusOrdersCommonResponseCollection) {
                setPlkHistory(plusOrdersCommonResponseCollection);
                dispatch({
                    type: StorageReducerAction.LOAD_STORAGE_HISTORY_NOP,
                });
                return;
            }
            firstLoad(dispatch, filters, orders);
        }
    }, [dispatch, filters, orders, type, plusOrdersCommonResponseCollection, plkHistory]);

    const pagginationCallback = (uri: string): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.paginationCallWithCallback(
            uri,
            'load_mnp_storage_history_list',
            (data: PlusOrdersCommonResponseCollectionInterface): void => {
                dispatch({
                    type: StorageReducerAction.LOAD_STORAGE_HISTORY_PLK_LIST,
                    plusOrdersCommonResponseCollection: data,
                });
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const getMvnoBlockListName = (mvnoBlockListNameId: number): string | null => {
        for (let i = 0; i < mvnoBlockList['hydra:member'].length; i++) {
            if (mvnoBlockList['hydra:member'][i].id === mvnoBlockListNameId) {
                return mvnoBlockList['hydra:member'][i].blockName;
            }
        }
        return null;
    };

    const getPlatformResellerName = (platformResellerId: number): string | null => {
        if (!platformResellerColection['hydra:member']) {
            return null;
        }
        for (let i = 0; i < platformResellerColection['hydra:member'].length; i++) {
            if (platformResellerColection['hydra:member'][i].id === platformResellerId) {
                return platformResellerColection['hydra:member'][i].mainname;
            }
        }
        return null;
    };

    const callViewBlockOperations = (params: { blockId: number }, event?: SyntheticEvent): void => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        history.respectModifierPush(
            event,
            FormGeneralHelper.getRouteUrl('/block-request-detail/adescom-api/api/platform/mvno_block_requests/' + params.blockId)
        );
    };

    const callViewSimCard = (params: { uri: string | null }, event?: SyntheticEvent): void => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        history.respectModifierPush(event, FormGeneralHelper.getRouteUrl('/sim-card-edit' + params.uri));
    };

    return (
        <React.Fragment>
            <TableBody>
                {plkHistory &&
                    plkHistory['hydra:member'] &&
                    plkHistory['hydra:member'].map((item) => {
                        let errorStatus = 'ok';
                        if (item.errorStatus) {
                            errorStatus = 'error';
                        }
                        return (
                            <TableRow key={'plus_common_response_id_' + item.id} classes={tableRowRoot} hover>
                                <TableCell classes={tableCellSticky} padding="none" align="right" style={{ paddingLeft: 0 }}>
                                    <ViewButton
                                        callback={(): void => viewPlkMessage(item)}
                                        tooltipTitle="prov:list.view"
                                        tooltipPlacement="bottom-start"
                                        callbackParam={{}}
                                    />
                                    <SimCardButton
                                        callback={callViewSimCard}
                                        tooltipTitle="prov:list.msisdn_view"
                                        tooltipPlacement="bottom-start"
                                        callbackParam={{ uri: item.simCard }}
                                    />
                                    {Auth.getUserType() === tokenInfo.SYSTEM_TYPE_MVNO_ADMIN && item.mvnoBlockListId && (
                                        <BlockOperationButton
                                            callback={callViewBlockOperations}
                                            tooltipTitle="prov:list.block_list"
                                            tooltipPlacement="bottom-start"
                                            callbackParam={{ blockId: item.mvnoBlockRequestId }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="left">
                                    {item.id}
                                </TableCell>
                                {/*{Auth.getUserType() === tokenInfo.SYSTEM_TYPE_MVNO_ADMIN && (*/}
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {getPlatformResellerName(item.platformResellerId)}
                                </TableCell>
                                {/*)}*/}
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {item.mvnoBlockRequestId}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {item.mvnoBlockListId && <I18n trans={'prov:b.' + getMvnoBlockListName(item.mvnoBlockListId)} />}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {item.command}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {item.imsi}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {DateHelper.format(item.requestTime)}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {DateHelper.format(item.responseTime)}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    <I18n trans={'prov:state.' + errorStatus} />
                                </TableCell>
                            </TableRow>
                        );
                    })}
            </TableBody>
            <TableFooter>
                <TableRow>
                    <TableCell colSpan={4}>
                        {plkHistory && plkHistory['hydra:view'] && <PagerRange callback={pagginationCallback} views={plkHistory} />}
                    </TableCell>
                </TableRow>
            </TableFooter>
        </React.Fragment>
    );
};
